/**
 * Which host implements which part of the contract — the single source of truth
 * for `fx-widget/1` capability parity.
 *
 * ## Why this file exists
 *
 * `contracts.ts` says what an operation *is*. It says nothing about who answers
 * it, and there are two hosts that must:
 *
 *  - **angular** the in-app Angular host
 *                (`apps/finxone-web-frontend/.../custom-widget/`), which answers
 *                a widget's `api.call` from the platform's own services.
 *  - **bff**     the Application BFF (`apps/account-service/src/application/`),
 *                which answers the same calls over HTTP for a widget running in
 *                a partner's page.
 *
 * The ids were `app` and `embed` until 2026-08-25. `app` meant the Angular host,
 * which read as the opposite of what `app/*` — the BFF's own route family — now
 * means; `angular` and `bff` name the two implementations without that clash.
 *
 * Before this registry the two drifted silently: an author read the SDK docs,
 * shipped a widget, and found out on the partner's site that the operation the
 * Angular host answers is `unknown-operation` on the BFF. Nothing failed at
 * build time, because nothing anywhere compared the two implementations.
 *
 * So: every operation and topic in the contract declares, here, whether each host
 * implements it — and when it does not, **why**. Two mechanisms keep that honest:
 *
 * 1. **Exhaustive by construction.** The registries are mapped types over
 *    `keyof AppApiOperations` / `keyof AppDataTopics`, so adding an
 *    operation to the contract without a support entry does not compile, and an
 *    entry for an operation that no longer exists does not compile either. The
 *    same applies to hosts: adding one to `APP_HOSTS` fails every entry until
 *    it is answered for.
 * 2. **Enforced against the real implementations.** Each host has a parity spec
 *    that derives its *implemented* set by probing the actual dispatcher — not by
 *    restating a list — and fails when it differs from what is declared here.
 *    See `application-support-parity.spec.ts` (account-service) and
 *    `custom-widget-support-parity.spec.ts` (web frontend).
 *
 * ## What "supported" means, precisely
 *
 * `supported: true` for a host means *that host's own dispatcher answers it*:
 * for `angular`, a case in the api-operations bridge (or one of its
 * sub-bridges); for `bff`, an entry on the BFF's v1 surface that
 * `ApplicationOperationsController` handles. It is a statement about the host,
 * not about the widget's grants — a widget still only reaches what
 * `CapabilityGrants.api` lists.
 *
 * ## This is a mirror, never a lever
 *
 * Nothing here changes what a host can do. If a host is missing something, the
 * entry records it as unsupported with a reason; making the reason go away means
 * implementing the operation in that host, not editing this file. A registry
 * edited to make a test pass is worth less than no registry at all.
 */
import type { AppApiOperations, AppDataTopics } from './contracts';

/**
 * The hosts that implement `fx-widget/1`. Adding one here is deliberately a
 * breaking change for both registries below: a new host must answer for every
 * operation and topic before it ships, which is the whole point.
 */
export const APP_HOSTS = ['angular', 'bff'] as const;
export type AppHostId = (typeof APP_HOSTS)[number];

/** Human-facing labels, for docs and tooling that render the matrix. */
export const APP_HOST_LABELS: Readonly<Record<AppHostId, string>> = {
  angular: 'Angular customer app host',
  bff: 'Application BFF (app/*)',
};

/**
 * The ids these hosts answered to before 2026-08-25, accepted for one release
 * by the lookup helpers so a caller outside this repo (a docs generator, a
 * partner's tooling) that still passes `app`/`embed` keeps working. Nothing in
 * this repo should use them: the registries below are keyed by the new ids and
 * a stale key does not compile.
 */
export const APP_HOST_ALIASES: Readonly<Record<string, AppHostId>> = {
  app: 'angular',
  embed: 'bff',
};

/** Resolve a host id, accepting the withdrawn `app`/`embed` spellings. */
function resolveHost(host: AppHostId | keyof typeof APP_HOST_ALIASES): AppHostId {
  return APP_HOST_ALIASES[host as string] ?? (host as AppHostId);
}

/**
 * One host's answer for one contract entry. Unsupported carries a reason
 * because "no" without a reason is indistinguishable from an oversight, and
 * an oversight is exactly the thing this registry exists to surface.
 */
export type HostSupport =
  { readonly supported: true } | { readonly supported: false; readonly reason: string };

export type SupportByHost = { readonly [Host in AppHostId]: HostSupport };

const ANGULAR_COMPANION = 'The AI companion has no Angular widget bridge; use the App SDK BFF transport.';
const ANGULAR_SUPPORT_MESSAGING = 'Customer support messaging has no Angular widget bridge; use the App SDK BFF transport.';

const YES: HostSupport = { supported: true };

function no(reason: string): HostSupport {
  return { supported: false, reason };
}

/* ──────────────────── shared reasons (BFF, operations) ──────────────────── */

/**
 * There is deliberately **no generic "not built yet" reason** in this file any
 * more.
 *
 * There used to be, and it covered twenty-eight operations. That is a fine
 * answer for a surface that is mid-build and a terrible one for a surface that
 * has stopped growing: it reads identically whether the operation is a morning's
 * work or is structurally impossible, so nobody reading the registry could tell
 * which. Every remaining `false` below therefore names the specific thing that
 * stops it — a native DTO the contract cannot satisfy, a route with no
 * organisation scoping, a guard the route does not carry, a host surface that
 * does not exist — and says what would have to change.
 *
 * If a future operation genuinely is only "not built yet", write that, in those
 * words, with the reason it has not been. Do not reintroduce a shared constant
 * that lets twenty-eight different situations share one sentence.
 */

/**
 * `cards.reveal` and `onboarding.verifyIdentity`. Both are host-rendered by
 * design — the secret, or the vendor identity session, is painted in *host* DOM
 * and never crosses to the widget. A BFF endpoint alone cannot serve them: the
 * app-embed runtime page has no host-owned overlay yet, and adding an endpoint that
 * returned the value instead would be the exfiltration the contract forbids.
 */
/**
 * Device enrolment on the Angular host belongs to the app shell — it owns the keystore key and
 * enrols it during login — so there is no widget-reachable bridge to it, and adding one would
 * let a widget mint an authentication factor for the signed-in customer.
 */
const ANGULAR_DEVICE_IS_THE_SHELL =
  'the app shell enrols its own keystore key during login; a widget cannot reach that key, and ' +
  'a bridge that enrolled an arbitrary public key would let a widget mint an authentication factor';

const ANGULAR_BIOMETRIC_IS_THE_SHELL =
  'a biometric credential is a Keycloak offline token held in a device keystore by the app ' +
  'itself; a widget has no keystore, and handing one a long-lived credential would defeat the ' +
  'point of the vault';

const ANGULAR_PUSH_IS_THE_SHELL =
  'a push token is minted by the OS for an installed binary; the Angular host is a page in a ' +
  'browser and has none, and the native shell registers its own Firebase token directly rather ' +
  'than through any widget bridge';

const ANGULAR_CARD_LIFECYCLE =
  'Standalone App SDK operation; the Angular widget host has no card activate, cancel, replace or limit-change bridge';
const ANGULAR_CARD_SECRETS =
  'Standalone App SDK operation for device-side card cryptography; a widget must never see PAN, ' +
  'CVV or PIN, and the Angular app keeps its own sensitive-details journey';

const ANGULAR_WALLET_IS_THE_SHELL =
  'the app shell drives PassKit / TapAndPay itself, against a native module no widget can ' +
  'reach; a bridge case would let a widget ask the issuer for an encrypted pass on behalf of a ' +
  'wallet it cannot see';

/**
 * `identity.verification.start` hands its caller a launchable vendor session (a Veriff session
 * URL or a Sumsub access token). A code application is its own host and may hold its customer's
 * session; a widget on the Angular host must never — that is the exfiltration
 * `onboarding.verifyIdentity`'s host-owned overlay exists to prevent.
 */
const ANGULAR_WIDGET_NEVER_HOLDS_A_VENDOR_SESSION =
  'the result is a bearer vendor identity session (a Veriff session URL or a Sumsub access ' +
  'token); a widget on the Angular host must never hold one, so the host keeps ' +
  'onboarding.verifyIdentity, which paints the vendor flow in host DOM and returns only ' +
  '{ launched: true }. Application-only: a code application is its own host.';

const BFF_NO_HOST_OVERLAY =
  'requires a host-rendered overlay the embed runtime page does not have yet: the value (or the ' +
  'vendor session) is painted in host DOM and never crosses to the widget, so this cannot be ' +
  'served by adding a BFF endpoint — the endpoint would have to return the very thing the ' +
  'contract forbids returning.';

/**
 * The auth group. There is a session on the BFF, but it is not one this host can
 * report on or change: the end user is authenticated by a partner-minted application
 * token whose lifetime the partner's backend owns. `authenticated` would be a
 * constant `true`, there is no project sign-in page to navigate to, and a logout
 * would revoke something the partner issued.
 */
/**
 * The page-state selection family (`beneficiaries.select`, `accounts.select`,
 * `accounts.selectCurrency`, `cards.select`, `savings.accounts.select`). One
 * constant because it is genuinely one situation, not twenty-eight: each of
 * these writes the app's Ngxs page state, which only the in-app host has.
 */
const BFF_NO_PAGE_STATE =
  'the selection lives in the Finxone app’s page state (the Ngxs context the native widgets on ' +
  'subsequent pages read); an embedded widget runs on a partner page where that state, and the ' +
  'native widgets that would read it, do not exist. Hold the choice in widget state and act on it ' +
  'directly instead.';

/**
 * The `records.*` family. The Angular host serves the very same Data Studio
 * documents, but as a host API (`sdk.storage.get/set/remove/catalog`, grant-checked
 * per definition against the widget's activation) rather than as operations — an
 * in-app widget has a host object to call, so it never needed them on the api
 * surface. A code application has one transport and no host object, which is why
 * the operation form exists. Adding these five cases to the Angular bridge would
 * give an in-app widget a second route to storage that bypasses the per-widget
 * grant its host API enforces; the storage API is the route there. The same
 * reasoning covers the `records.items.*` five: collections have no
 * `sdk.storage` twin at all, so an in-app widget has no route to them and
 * none is being added behind the grant's back.
 */
const ANGULAR_STORAGE_IS_A_HOST_API =
  'the in-app host serves these very documents through the storage host API ' +
  '(sdk.storage.get/set/remove/catalog), grant-checked per definition against the widget’s own ' +
  'activation. Adding them to the api bridge would hand an in-app widget a second route to the ' +
  'same documents that skips that per-widget grant. Use sdk.storage.* in a widget; the operation ' +
  'form exists for code applications, which have one transport and no host object.';

/* ══════════════════════════════════════════════════════════════════════════
 * OPERATIONS
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Host support for every entry in `AppApiOperations`.
 *
 * Exhaustive by construction — see the file docs. Do not "fix" a failing parity
 * spec by flipping an entry to `YES`; flip it when the host actually answers it.
 */
export const APP_OPERATION_SUPPORT: {
  readonly [Operation in keyof AppApiOperations]: SupportByHost;
} = {
  'businessPermissions.status': { angular: { supported: false, reason: 'Business permissions require an SDK-only project and the App SDK BFF transport.' }, bff: YES },
  'businessPermissions.enable': { angular: { supported: false, reason: 'Business permissions require an SDK-only project and the App SDK BFF transport.' }, bff: YES },
  'businessPermissions.departments.list': { angular: { supported: false, reason: 'Business permissions require an SDK-only project and the App SDK BFF transport.' }, bff: YES },
  'businessPermissions.departments.create': { angular: { supported: false, reason: 'Business permissions require an SDK-only project and the App SDK BFF transport.' }, bff: YES },
  'businessPermissions.members.list': { angular: { supported: false, reason: 'Business permissions require an SDK-only project and the App SDK BFF transport.' }, bff: YES },
  'businessPermissions.members.set': { angular: { supported: false, reason: 'Business permissions require an SDK-only project and the App SDK BFF transport.' }, bff: YES },
  'businessPermissions.members.remove': { angular: { supported: false, reason: 'Business permissions require an SDK-only project and the App SDK BFF transport.' }, bff: YES },
  'businessPermissions.resources.list': { angular: { supported: false, reason: 'Business permissions require an SDK-only project and the App SDK BFF transport.' }, bff: YES },
  'businessPermissions.resources.assign': { angular: { supported: false, reason: 'Business permissions require an SDK-only project and the App SDK BFF transport.' }, bff: YES },
  'businessPermissions.resources.remove': { angular: { supported: false, reason: 'Business permissions require an SDK-only project and the App SDK BFF transport.' }, bff: YES },
  'businessPermissions.grants.list': { angular: { supported: false, reason: 'Business permissions require an SDK-only project and the App SDK BFF transport.' }, bff: YES },
  'businessPermissions.grants.set': { angular: { supported: false, reason: 'Business permissions require an SDK-only project and the App SDK BFF transport.' }, bff: YES },
  'businessPermissions.grants.remove': { angular: { supported: false, reason: 'Business permissions require an SDK-only project and the App SDK BFF transport.' }, bff: YES },
  'connections.connect': { angular: no('Customer OAuth uses the App SDK BFF transport.'), bff: YES },
  'connections.status': { angular: no('Customer OAuth uses the App SDK BFF transport.'), bff: YES },
  'connections.disconnect': { angular: no('Customer OAuth uses the App SDK BFF transport.'), bff: YES },
  /* project-selected registry and address lookups */
  'companies.search': { angular: YES, bff: YES },
  'companies.get': { angular: YES, bff: YES },
  'addresses.suggest': { angular: YES, bff: YES },
  'addresses.resolve': { angular: YES, bff: YES },
  'lookups.list': { angular: YES, bff: YES },

  /* payments */
  'payments.recurring.create': { angular: no('Recurring outbound payments require the BFF transport'), bff: YES },
  'payments.recurring.list': { angular: no('Recurring outbound payments require the BFF transport'), bff: YES },
  'payments.recurring.update': { angular: no('Recurring outbound payments require the BFF transport'), bff: YES },
  'payments.recurring.cancel': { angular: no('Recurring outbound payments require the BFF transport'), bff: YES },
  'payments.initiate': { angular: YES, bff: YES },
  'payments.reasons.list': { angular: YES, bff: YES },
  'payments.scheduled.list': { angular: YES, bff: YES },
  // The Angular host's api bridge has no case for it: the platform's own screens read
  // `GET /payments/scheduled` through AccountService directly, never through `api.call`.
  'payments.scheduled.upcoming': {
    angular: no('the Angular host reads this listing through its own AccountService, not the widget api bridge'),
    bff: YES,
  },
  'payments.scheduled.update': { angular: no('Retail schedule amendments require the BFF transport'), bff: YES },
  'payments.scheduled.cancel': { angular: YES, bff: YES },
  'payments.precheckInternational': { angular: YES, bff: YES },
  'payments.initiateInternational': { angular: YES, bff: YES },
  'payments.internalTransfer': {
    angular: no('Standalone App SDK operation; the Angular widget host has no internal-transfer route'),
    bff: YES,
  },
  'payments.schemes.list': { angular: YES, bff: YES },

  /* beneficiaries */
  'beneficiaries.cop.check': { angular: YES, bff: YES },
  'beneficiaries.create': { angular: YES, bff: YES },
  'beneficiaries.updateNickname': { angular: no('The Angular widget host has no nickname-update operation'), bff: YES },
  'beneficiaries.list': { angular: YES, bff: YES },
  'beneficiaries.detail': { angular: no('Standalone App SDK operation; no Angular widget bridge route'), bff: YES },
  'beneficiaries.update': { angular: no('Standalone App SDK operation; no Angular widget bridge route'), bff: YES },
  'beneficiaries.favourite': { angular: no('Standalone App SDK operation; no Angular widget bridge route'), bff: YES },
  'beneficiaries.favourites': { angular: no('Standalone App SDK operation; no Angular widget bridge route'), bff: YES },
  'beneficiaries.recent': { angular: no('Standalone App SDK operation; no Angular widget bridge route'), bff: YES },

  'beneficiaries.select': { angular: YES, bff: no(BFF_NO_PAGE_STATE) },
  'beneficiaries.delete': {
    angular: no('Standalone App SDK operation; the Angular widget host has no beneficiary-delete route'),
    bff: YES,
  },
  'beneficiaries.requirements': { angular: YES, bff: YES },
  'beneficiaries.createInternational': { angular: YES, bff: YES },
  'beneficiaries.validate': {
    angular: no('Standalone App SDK operation; the Angular widget host has no beneficiary-validate route'),
    bff: YES,
  },

  /* accounts & transactions */
  'accounts.capabilities': { angular: YES, bff: YES },
  'accounts.get': { angular: no('The Angular customer app has no direct account-detail endpoint; use accounts.list.'), bff: YES },
  'accounts.create': { angular: YES, bff: YES },
  'accounts.rename': { angular: YES, bff: YES },
  'accounts.close': { angular: YES, bff: YES },
  'exchange.rates': { angular: YES, bff: YES },
  'exchange.refresh': { angular: YES, bff: YES },
  'exchange.transactions.list': { angular: YES, bff: YES },
  'exchange.transactions.get': { angular: YES, bff: YES },
  'accounts.list': { angular: YES, bff: YES },
  'accounts.select': { angular: YES, bff: no(BFF_NO_PAGE_STATE) },
  'accounts.selectCurrency': { angular: YES, bff: no(BFF_NO_PAGE_STATE) },
  'transactions.list': { angular: YES, bff: YES },
  'transactions.detail': { angular: YES, bff: YES },

  /* payment requests */
  'paymentRequests.list': { angular: YES, bff: YES },
  'paymentRequests.get': { angular: YES, bff: YES },
  'paymentRequests.create': { angular: YES, bff: YES },
  'paymentRequests.approve': {
    angular: no('Use the App SDK BFF transport for payment-request approval.'),
    bff: YES,
  },
  'paymentRequests.reject': {
    angular: no('Use the App SDK BFF transport for payment-request rejection.'),
    bff: YES,
  },

  /* bulk payments */
  'bulkPayments.create': {
    angular: no('Use the App SDK BFF transport for bulk-payment preparation and execution.'),
    bff: YES,
  },
  'bulkPayments.addBeneficiaries': {
    angular: no('Use the App SDK BFF transport for bulk-payment preparation and execution.'),
    bff: YES,
  },
  'bulkPayments.removeTransfer': {
    angular: no('Use the App SDK BFF transport for bulk-payment preparation and execution.'),
    bff: YES,
  },
  'bulkPayments.updatePayee': {
    angular: no('Use the App SDK BFF transport for bulk-payment preparation and execution.'),
    bff: YES,
  },
  'bulkPayments.requestApproval': {
    angular: no('Use the App SDK BFF transport for bulk-payment preparation and execution.'),
    bff: YES,
  },
  'bulkPayments.execute': {
    angular: no('Use the App SDK BFF transport for bulk-payment preparation and execution.'),
    bff: YES,
  },
  'bulkPayments.cancel': {
    angular: no('Use the App SDK BFF transport for bulk-payment preparation and execution.'),
    bff: YES,
  },
  'bulkPayments.rescheduleTransfer': { angular: no('Use the App SDK BFF transport for scheduled bulk payments.'), bff: YES },
  'bulkPayments.cancelScheduledTransfer': { angular: no('Use the App SDK BFF transport for scheduled bulk payments.'), bff: YES },
  'bulkPayments.list': { angular: YES, bff: YES },
  'bulkPayments.get': { angular: YES, bff: YES },
  'bulkPayments.retry': { angular: YES, bff: YES },

  /* cards */
  'cards.transactions.list': { angular: no('Selected-card transactions require the BFF transport'), bff: YES },
  'cards.list': { angular: YES, bff: YES },
  'cards.orderOptions': { angular: no('Use the App SDK BFF transport for physical card ordering.'), bff: YES },
  // Angular retains virtual issuance and explicitly refuses physical requests.
  'cards.create': { angular: YES, bff: YES },
  'cards.select': { angular: YES, bff: no(BFF_NO_PAGE_STATE) },
  'cards.freeze': { angular: YES, bff: YES },
  'cards.unfreeze': { angular: YES, bff: YES },
  'cards.authentications.list': { angular: no('Use the App BFF for card authentication challenges'), bff: YES },
  'cards.authentications.get': { angular: no('Use the App BFF for card authentication challenges'), bff: YES },
  'cards.authentications.approve': { angular: no('Use the App BFF for card authentication challenges'), bff: YES },
  'cards.authentications.reject': { angular: no('Use the App BFF for card authentication challenges'), bff: YES },
  'cards.limits.get': { angular: YES, bff: YES },
  // The Angular host keeps its own sensitive-details journey (the native `sensitive-details/session`).
  'cards.secureKey': { angular: no(ANGULAR_CARD_SECRETS), bff: YES },
  'cards.secureDetails': { angular: no(ANGULAR_CARD_SECRETS), bff: YES },
  'cards.pin.get': { angular: no(ANGULAR_CARD_SECRETS), bff: YES },
  'cards.pin.set': { angular: no(ANGULAR_CARD_SECRETS), bff: YES },
  'cards.threeDsPassword.set': { angular: no(ANGULAR_CARD_SECRETS), bff: YES },
  'cards.activate': { angular: no(ANGULAR_CARD_LIFECYCLE), bff: YES },
  'cards.cancel': { angular: no(ANGULAR_CARD_LIFECYCLE), bff: YES },
  'cards.replace': { angular: no(ANGULAR_CARD_LIFECYCLE), bff: YES },
  'cards.limits.set': { angular: no(ANGULAR_CARD_LIFECYCLE), bff: YES },
  'keelmoney.cards.controls.get': { angular: no('Standalone App SDK operation; the Angular widget host has no Keel card controls'), bff: YES },
  'keelmoney.cards.controls.set': { angular: no('Standalone App SDK operation; the Angular widget host has no Keel card controls'), bff: YES },

  /* BACS Direct Debit mandates */
  'directDebits.list': { angular: no('Standalone App SDK operation; the Angular widget host has no Direct Debit journey'), bff: YES },
  'directDebits.get': { angular: no('Standalone App SDK operation; the Angular widget host has no Direct Debit journey'), bff: YES },
  'directDebits.cancel': { angular: no('Standalone App SDK operation; the Angular widget host has no Direct Debit journey'), bff: YES },

  /* accounts at other banks, linked through an aggregator */
  'externalAccounts.list': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.get': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.connections.list': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.balances': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.transactions': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.connect': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.reconnect': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.complete': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.disconnect': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.sync': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.refresh': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.banks.list': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.payments.create': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.payments.complete': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.payments.get': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.payments.list': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.mandates.banks': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.mandates.create': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.mandates.complete': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.mandates.list': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.mandates.cancel': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.mandates.get': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.mandates.refresh': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.mandates.collections.create': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.mandates.collections.get': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'externalAccounts.mandates.collections.list': { angular: no('Standalone App SDK operation; the Angular widget host has no external-accounts journey'), bff: YES },
  'budgets.get': { angular: no('Standalone App SDK operation; Angular uses its existing spending-budget widget'), bff: YES },
  'budgets.set': { angular: no('Standalone App SDK operation; Angular uses its existing spending-budget widget'), bff: YES },
  'budgets.clear': { angular: no('Standalone App SDK operation; Angular uses its existing spending-budget widget'), bff: YES },
  'budgets.status': { angular: no('Standalone App SDK operation; Angular uses its existing spending-budget widget'), bff: YES },
  'pots.list': { angular: no('Standalone App SDK operation; the Angular widget host has no savings pots journey'), bff: YES },
  'pots.transactions.list': { angular: no('Standalone App SDK operation; the Angular widget host has no savings pots journey'), bff: YES },
  'pots.create': { angular: no('Standalone App SDK operation; the Angular widget host has no savings pots journey'), bff: YES },
  'pots.rename': { angular: no('Standalone App SDK operation; the Angular widget host has no savings pots journey'), bff: YES },
  'pots.setTarget': { angular: no('Standalone App SDK operation; the Angular widget host has no savings pots journey'), bff: YES },
  'pots.close': { angular: no('Standalone App SDK operation; the Angular widget host has no savings pots journey'), bff: YES },
  'pots.transfer': { angular: no('Standalone App SDK operation; the Angular widget host has no savings pots journey'), bff: YES },

  /* wallet push provisioning */
  // Same shape as the device family: the Angular host is the app shell, and the
  // provisioning ceremony belongs to the shell's own PassKit / TapAndPay code —
  // there is no `api.call` case for it, and a widget that could ask for an
  // encrypted pass would be asking on behalf of a wallet it cannot see. A code
  // application IS the shell there, and it holds the native module itself.
  'cards.wallet.beginProvisioning': { angular: no(ANGULAR_WALLET_IS_THE_SHELL), bff: YES },
  'cards.wallet.completeProvisioning': { angular: no(ANGULAR_WALLET_IS_THE_SHELL), bff: YES },

  /* savings */
  'savings.products.list': { angular: YES, bff: YES },
  'savings.products.listForCustomer': { angular: YES, bff: YES },
  'savings.accounts.list': { angular: YES, bff: YES },
  'savings.accounts.saascada.balanceBreakdown': { angular: no('Standalone App SDK operation; the Angular widget host does not expose balance breakdowns'), bff: YES },
  'savings.accounts.get': { angular: no('Standalone App SDK operation; the Angular widget host does not expose this savings journey'), bff: YES },
  'savings.balance.total': { angular: no('Standalone App SDK operation; the Angular widget host does not expose the organisation wealth read'), bff: YES },
  'savings.accounts.page': { angular: no('Standalone App SDK operation; the Angular widget host does not expose this savings journey'), bff: YES },
  'savings.transactions.list': { angular: no('Standalone App SDK operation; the Angular widget host does not expose this savings journey'), bff: YES },
  'savings.requests.kyc': { angular: no('Standalone App SDK operation; the Angular widget host does not expose this savings journey'), bff: YES },
  'savings.vrp.list': { angular: no('Standalone App SDK operation; the Angular widget host does not expose this savings journey'), bff: YES },
  'savings.vrp.cancel': { angular: no('Standalone App SDK operation; the Angular widget host does not expose this savings journey'), bff: YES },
  'savings.nominated.accounts': { angular: no('Standalone App SDK operation; the Angular widget host does not expose this savings journey'), bff: YES },
  'savings.maturity.setAdvanced': { angular: no('Standalone App SDK operation; the Angular widget host does not expose this savings journey'), bff: YES },
  'savings.accounts.select': { angular: YES, bff: no(BFF_NO_PAGE_STATE) },
  'savings.create': { angular: YES, bff: YES },
  'savings.close': { angular: YES, bff: YES },
  'savings.internalTransfer': { angular: YES, bff: YES },
  'savings.withdraw': { angular: YES, bff: YES },
  'savings.maturity.set': { angular: YES, bff: YES },
  // Both hosts hit the routes the native savings listing already used: PATCH
  // /savings/:accountId for the rename, GET /savings/account-requests for the
  // pending applications.
  'savings.accounts.rename': { angular: YES, bff: YES },
  'savings.requests.list': { angular: YES, bff: YES },

  /* the nominated account: the destination every savings payout already used */
  'savings.nominated.get': { angular: YES, bff: YES },
  'savings.nominated.institutions': { angular: YES, bff: YES },
  'savings.nominated.connect': { angular: YES, bff: YES },
  'savings.nominated.process': { angular: YES, bff: YES },
  // The Angular host drives its custom payment pages through its own services and the URL
  // (`billing_request_id`), not through widget operations — so these are BFF-only.
  'savings.pisp.institutions': {
    angular: no('the Angular host lists PISP banks through its own SavingsAccountService, not a widget operation'),
    bff: YES,
  },
  'savings.pisp.setInstitution': {
    angular: no('the Angular host sets the PISP institution from its own banks widget, not a widget operation'),
    bff: YES,
  },
  'savings.pisp.prepare': {
    angular: no('the Angular host mints the billing request itself and carries it in the URL'),
    bff: YES,
  },
  // No `savings.nominated.update` row, because there is no such operation: hand-entered
  // nominated accounts were banned on 2026-08-27 (see contracts.ts at the catalogue site).

  /* the customer's own details */
  'profile.picture.get': { angular: no('Profile pictures are available through the code application gateway.'), bff: YES },
  'organisation.get': { angular: no('The organisation record is available through the code application gateway.'), bff: YES },
  'profile.picture.set': { angular: no('Profile pictures are available through the code application gateway.'), bff: YES },
  'profile.update': { angular: YES, bff: YES },
  // Both hosts go to org-metadata-service, which owns the current-password check and the
  // realm's password policy. The Angular host posts the customer's own session to
  // `POST /users/reset-password`; the BFF cannot (that route is confined away from
  // application tokens) and states the subject over the in-cluster route instead.
  'profile.changePassword': { angular: YES, bff: YES },

  /* exchange */
  'exchange.quote': { angular: YES, bff: YES },
  'exchange.accept': { angular: YES, bff: YES },

  /* documents */
  'documents.download': { angular: YES, bff: YES },
  'documents.list': { angular: YES, bff: YES },
  'documents.upload': { angular: YES, bff: YES },
  'documents.delete': { angular: YES, bff: YES },

  /* the in-app notification feed */
  'support.subjects.list': { angular: no(ANGULAR_SUPPORT_MESSAGING), bff: YES },
  'support.welcome.get': { angular: no(ANGULAR_SUPPORT_MESSAGING), bff: YES },
  'support.threads.list': { angular: no(ANGULAR_SUPPORT_MESSAGING), bff: YES },
  'support.threads.get': { angular: no(ANGULAR_SUPPORT_MESSAGING), bff: YES },
  'support.threads.create': { angular: no(ANGULAR_SUPPORT_MESSAGING), bff: YES },
  'support.messages.list': { angular: no(ANGULAR_SUPPORT_MESSAGING), bff: YES },
  'support.messages.send': { angular: no(ANGULAR_SUPPORT_MESSAGING), bff: YES },
  'support.markRead': { angular: no(ANGULAR_SUPPORT_MESSAGING), bff: YES },
  'support.unreadCount': { angular: no(ANGULAR_SUPPORT_MESSAGING), bff: YES },
  'support.typing': { angular: no(ANGULAR_SUPPORT_MESSAGING), bff: YES },
  'onboarding.companion.chat': { angular: no(ANGULAR_COMPANION), bff: YES },
  'onboarding.journey.get': { angular: no(ANGULAR_COMPANION), bff: YES },
  'onboarding.answers.save': { angular: no(ANGULAR_COMPANION), bff: YES },
  'onboarding.identity.create': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.beneficiary.extractImage': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.invoice.extractDocument': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.chat': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.decide': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.memories.list': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.memories.delete': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.memories.settings': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.actions.list': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.preferences.get': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.preferences.update': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.sessions.list': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.sessions.get': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.sessions.delete': { angular: no(ANGULAR_COMPANION), bff: YES },
  'companion.insights.get': { angular: no(ANGULAR_COMPANION), bff: YES },
  'rules.create': { angular: no(ANGULAR_COMPANION), bff: YES },
  'rules.list': { angular: no(ANGULAR_COMPANION), bff: YES },
  'rules.cancel': { angular: no(ANGULAR_COMPANION), bff: YES },
  'rules.pause': { angular: no(ANGULAR_COMPANION), bff: YES },
  'rules.resume': { angular: no(ANGULAR_COMPANION), bff: YES },
  'rules.update': { angular: no(ANGULAR_COMPANION), bff: YES },
  'rules.executions.list': { angular: no(ANGULAR_COMPANION), bff: YES },
  'notifications.preferences.get': { angular: no('Standalone App SDK notification preferences'), bff: YES },
  'notifications.preferences.update': { angular: no('Standalone App SDK notification preferences'), bff: YES },
  'notifications.settings.get': { angular: no('Standalone App SDK Notification Policy settings'), bff: YES },
  'notifications.settings.update': { angular: no('Standalone App SDK Notification Policy settings'), bff: YES },
  // Both hosts honour `read` and `unreadCount`: the BFF through Novu's inbox routes, Angular
  // through its own @novu/js session (`notifications.list({ read })`, `notifications.count`).
  'notifications.list': { angular: YES, bff: YES },
  // The Angular host reads its feed through the browser's own Novu session (@novu/js), and
  // the only mark-read route the platform gives it is `POST /notifications/markAs`, which
  // marks an entire CHANNEL — there is no per-message form on it at all. So this host could
  // answer the no-`ids` call and could not answer the `ids` one, and an operation that
  // silently ignores half its payload is worse than one that says it is not here. It becomes
  // supported when the native route grows a message-id form (the Novu API has one:
  // POST /v1/subscribers/:id/messages/markAs, which is what the BFF uses).
  'notifications.markRead': {
    angular: no(
      'the only platform mark-read route this host has (POST /notifications/markAs) marks a ' +
        'whole channel and has no message-id form, so the `ids` half of the payload could not ' +
        'be honoured; the BFF calls Novu’s own per-message markAs instead',
    ),
    bff: YES,
  },
  // The push half of the feed. Same shape as the device family and for the same
  // reason: a token is minted by the OS for a *binary*, and the Angular host is a
  // page in a browser that has none. The native shell posts the very same body
  // (`POST notification/subscription-token`) from its own Firebase registration,
  // outside any widget bridge; a code application IS that binary, which is why
  // the operation form exists.
  'notifications.registerDevice': { angular: no(ANGULAR_PUSH_IS_THE_SHELL), bff: YES },
  'notifications.unregisterDevice': { angular: no(ANGULAR_PUSH_IS_THE_SHELL), bff: YES },

  /* device enrolment */
  // The Angular host does enrol devices — but as the app shell, at login and in its own
  // settings screens, against a key held by the native keystore that no widget can reach or
  // even name. There is no `api.call` case for it and there should not be one: a widget that
  // could enrol an arbitrary public key against the signed-in customer would be minting an
  // authentication factor. The application transport is different in kind — a code
  // application IS the shell there, and it holds the enclave key itself.
  'device.enroll': { angular: no(ANGULAR_DEVICE_IS_THE_SHELL), bff: YES },
  'device.trust.vouch.start': { angular: no(ANGULAR_DEVICE_IS_THE_SHELL), bff: YES },
  'device.trust.vouch.challenge': { angular: no(ANGULAR_DEVICE_IS_THE_SHELL), bff: YES },
  'device.trust.vouch.verify': { angular: no(ANGULAR_DEVICE_IS_THE_SHELL), bff: YES },
  'device.trust.vouch.finish': { angular: no(ANGULAR_DEVICE_IS_THE_SHELL), bff: YES },
  'device.trust.enrollment.start': { angular: no(ANGULAR_DEVICE_IS_THE_SHELL), bff: YES },
  'device.trust.enrollment.prepare': { angular: no(ANGULAR_DEVICE_IS_THE_SHELL), bff: YES },
  'device.trust.enrollment.challenge': { angular: no(ANGULAR_DEVICE_IS_THE_SHELL), bff: YES },
  'device.trust.enrollment.verify': { angular: no(ANGULAR_DEVICE_IS_THE_SHELL), bff: YES },
  'device.trust.enrollment.finish': { angular: no(ANGULAR_DEVICE_IS_THE_SHELL), bff: YES },
  'device.list': { angular: no(ANGULAR_DEVICE_IS_THE_SHELL), bff: YES },
  'device.remove': { angular: no(ANGULAR_DEVICE_IS_THE_SHELL), bff: YES },
  // A biometric credential is a Keycloak offline token for a device keystore. A widget has
  // neither the keystore nor any business holding a long-lived credential for the customer.
  'session.biometric.enroll': { angular: no(ANGULAR_BIOMETRIC_IS_THE_SHELL), bff: YES },
  'session.biometric.revoke': { angular: no(ANGULAR_BIOMETRIC_IS_THE_SHELL), bff: YES },

  /* onboarding */
  'onboarding.flow': { angular: YES, bff: YES },
  'onboarding.progress.save': { angular: no('New application drafts use the anonymous Application BFF; legacy form tracking is unchanged.'), bff: YES },
  'onboarding.progress.load': { angular: no('New application drafts use the anonymous Application BFF; legacy form tracking is unchanged.'), bff: YES },
  'onboarding.progress.complete': { angular: no('New application drafts use the anonymous Application BFF; legacy form tracking is unchanged.'), bff: YES },
  'onboarding.requirements': { angular: YES, bff: YES },
  'onboarding.customerMatch': { angular: YES, bff: YES },
  'onboarding.bank.verify': { angular: { supported: false, reason: 'The Angular host uses the legacy onboarding form action; this operation is served by the App client BFF transport.' }, bff: YES },
  'onboarding.verify.send': { angular: YES, bff: YES },
  'onboarding.verify.confirm': { angular: YES, bff: YES },
  'onboarding.submitIndividual': { angular: YES, bff: YES },
  'onboarding.submitBusiness': { angular: YES, bff: YES },
  'onboarding.verifyIdentity': { angular: YES, bff: no(BFF_NO_HOST_OVERLAY) },
  'identity.verification.start': { angular: no(ANGULAR_WIDGET_NEVER_HOLDS_A_VENDOR_SESSION), bff: YES },
  'identity.verification.profile': { angular: no('Retained identity documents are available only to the signed-in code application.'), bff: YES },

  /* auth */
  'auth.passwordPolicy': { angular: YES, bff: YES },
  'auth.status': { angular: YES, bff: YES },
  'auth.login': { angular: YES, bff: YES },
  'auth.logout': { angular: YES, bff: YES },

  /* managed login flow */
  'auth.flow.start': { angular: YES, bff: YES },
  'auth.flow.submit': { angular: YES, bff: YES },
  'auth.flow.selectMethod': { angular: YES, bff: YES },
  'auth.flow.cancel': { angular: YES, bff: YES },
  'providerCapabilities.list': { angular: YES, bff: YES },
  'providerCapabilities.invoke': { angular: YES, bff: YES },

  /* Data Studio records */
  'records.list': { angular: no(ANGULAR_STORAGE_IS_A_HOST_API), bff: YES },
  'records.get': { angular: no(ANGULAR_STORAGE_IS_A_HOST_API), bff: YES },
  'records.create': { angular: no(ANGULAR_STORAGE_IS_A_HOST_API), bff: YES },
  'records.update': { angular: no(ANGULAR_STORAGE_IS_A_HOST_API), bff: YES },
  'records.delete': { angular: no(ANGULAR_STORAGE_IS_A_HOST_API), bff: YES },
  'records.items.query': { angular: no(ANGULAR_STORAGE_IS_A_HOST_API), bff: YES },
  'records.items.get': { angular: no(ANGULAR_STORAGE_IS_A_HOST_API), bff: YES },
  'records.items.create': { angular: no(ANGULAR_STORAGE_IS_A_HOST_API), bff: YES },
  'records.items.update': { angular: no(ANGULAR_STORAGE_IS_A_HOST_API), bff: YES },
  'records.items.delete': { angular: no(ANGULAR_STORAGE_IS_A_HOST_API), bff: YES },
};

export type AppOperationName = keyof AppApiOperations;

/** Operations this host's dispatcher answers, sorted. Accepts the old ids. */
export function operationsSupportedBy(
  host: AppHostId | keyof typeof APP_HOST_ALIASES,
): AppOperationName[] {
  const id = resolveHost(host);
  return (Object.keys(APP_OPERATION_SUPPORT) as AppOperationName[])
    .filter((operation) => APP_OPERATION_SUPPORT[operation][id].supported)
    .sort();
}

/** Operations this host does not answer, with the declared reason. */
export function operationsUnsupportedBy(
  host: AppHostId | keyof typeof APP_HOST_ALIASES,
): { operation: AppOperationName; reason: string }[] {
  const id = resolveHost(host);
  return (Object.keys(APP_OPERATION_SUPPORT) as AppOperationName[])
    .map((operation) => ({ operation, support: APP_OPERATION_SUPPORT[operation][id] }))
    .filter(
      (entry): entry is { operation: AppOperationName; support: { supported: false; reason: string } } =>
        !entry.support.supported,
    )
    .map(({ operation, support }) => ({ operation, reason: support.reason }))
    .sort((left, right) => left.operation.localeCompare(right.operation));
}

/* ══════════════════════════════════════════════════════════════════════════
 * TOPICS — SEPARATE SECTION, DELETABLE WHOLESALE
 *
 * Everything from here to the end of the file concerns `AppDataTopics`, and
 * nothing above it depends on anything below it. If data topics are retired in
 * favour of operations, delete this block (and the topic half of each host's
 * parity spec) and the operation machinery is untouched.
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Host support for every entry in `AppDataTopics`. Same exhaustiveness
 * guarantee as the operation registry.
 *
 * For `bff`, "supported" means **the Application BFF answers it** — i.e. the topic is
 * on `APP_V1_TOPICS` and `ApplicationDataController` serves it. Two topics are
 * marked unsupported here that a widget nonetheless *receives* on the BFF, because
 * the runtime page answers them locally without a BFF round trip; their reasons
 * say so rather than leaving a reader to conclude the topic is unavailable.
 */
export const APP_TOPIC_SUPPORT: {
  readonly [Topic in keyof AppDataTopics]: SupportByHost;
} = {
  profile: { angular: YES, bff: YES },
  pageContext: {
    angular: YES,
    bff: no(
      'not a BFF topic, but the widget still receives it: the embed runtime answers pageContext ' +
        'locally from the wrapper’s `init` message. There is no fetch behind this topic, so a round ' +
        'trip could only echo what the runtime already holds.',
    ),
  },
  // Wave 4: on the BFF the records live SERVER-side (embed_handoff) rather than
  // in the runtime document — the page and the hand-off do not share a
  // lifetime there — and pending records are resolved against the provider on
  // every read, so a killed app never loses an outcome.
  handoffs: { angular: YES, bff: YES },
  onboardingStatus: { angular: YES, bff: YES },
  // The workflow app does not read its own alert copy through a data topic — it
  // holds the catalogue in NGXS (`ProjectAlertsState`) and resolves against that,
  // so there is nothing for the widget host to serve. The topic exists for the
  // surface that has no such store: a code application.
  alerts: {
    angular: no(
      'not a widget-host topic: the Angular runtime already holds the project’s alert catalogue in ' +
        'ProjectAlertsState and renders it itself, so a widget on that host never needs to fetch it.',
    ),
    bff: YES,
  },
};

export type AppTopicName = keyof AppDataTopics;

/** Topics this host serves, sorted. Accepts the old ids. */
export function topicsSupportedBy(
  host: AppHostId | keyof typeof APP_HOST_ALIASES,
): AppTopicName[] {
  const id = resolveHost(host);
  return (Object.keys(APP_TOPIC_SUPPORT) as AppTopicName[])
    .filter((topic) => APP_TOPIC_SUPPORT[topic][id].supported)
    .sort();
}

/** Topics this host does not serve, with the declared reason. */
export function topicsUnsupportedBy(
  host: AppHostId | keyof typeof APP_HOST_ALIASES,
): { topic: AppTopicName; reason: string }[] {
  const id = resolveHost(host);
  return (Object.keys(APP_TOPIC_SUPPORT) as AppTopicName[])
    .map((topic) => ({ topic, support: APP_TOPIC_SUPPORT[topic][id] }))
    .filter(
      (entry): entry is { topic: AppTopicName; support: { supported: false; reason: string } } =>
        !entry.support.supported,
    )
    .map(({ topic, support }) => ({ topic, reason: support.reason }))
    .sort((left, right) => left.topic.localeCompare(right.topic));
}

/* ─────────────────────────── rendering (docs) ──────────────────────────── */

/** One row of the author-facing support matrix. */
export interface AppSupportRow {
  /** 'operation' | 'topic' — so a renderer can split or merge the two. */
  kind: 'operation' | 'topic';
  name: string;
  /** Hosts that answer it, in `APP_HOSTS` order. */
  supportedBy: AppHostId[];
  /** Why each other host does not, keyed by host. */
  reasons: Partial<Record<AppHostId, string>>;
}

/**
 * The whole registry flattened for rendering — the shape docs tooling should
 * consume, so nobody hand-writes a table that rots.
 *
 * When topics go, `kind: 'topic'` rows stop appearing and every consumer keeps
 * working; delete the topic block above and this function still compiles.
 */
export function appSupportMatrix(): AppSupportRow[] {
  const row = (kind: 'operation' | 'topic', name: string, support: SupportByHost): AppSupportRow => {
    const reasons: Partial<Record<AppHostId, string>> = {};
    for (const host of APP_HOSTS) {
      const entry = support[host];
      if (!entry.supported) reasons[host] = entry.reason;
    }
    return {
      kind,
      name,
      supportedBy: APP_HOSTS.filter((host) => support[host].supported),
      reasons,
    };
  };
  return [
    ...(Object.keys(APP_OPERATION_SUPPORT) as AppOperationName[])
      .sort()
      .map((operation) => row('operation', operation, APP_OPERATION_SUPPORT[operation])),
    ...(Object.keys(APP_TOPIC_SUPPORT) as AppTopicName[])
      .sort()
      .map((topic) => row('topic', topic, APP_TOPIC_SUPPORT[topic])),
  ];
}
